import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import ShopType from "@/enum/ShopType";
import { ShipValidationRule, ShipValidator } from "@/services/ship/type";
import { buildValidationFailedResult, buildValidationSuccessfulResult } from "@/services/ship/util";
import GlobalStore from "@/store";
import { ordersGroupByShopId, shipValidate } from "@/utils/OrderUtils";
import ShopDTO from "@/dto/shop/ShopDTO";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

/** 需要全局查找这个的引用 */
export interface ShipWaybillValidateErrorData {
    orderId: number;
    orderIndex: number;
    orderOriginalIds: string;
    errorMessage: string;
}

interface WaybillValidationResult {
    validOrders: OrderDTO[];
    errors: ShipWaybillValidateErrorData[];
}

/**
 * 校验运单号
 */
export function getValidateWaybillNoErrorDataSource(
    currentShopType: ShopType,
    allOrders: OrderDTO[],
    selectedOrders: OrderDTO[],
): {
    errorDataSource: ShipWaybillValidateErrorData[];
    canShipOrders: OrderDTO[];
} {
    const errorDataSource: ShipWaybillValidateErrorData[] = [];
    const canShipOrders: OrderDTO[] = [];

    // 手工添加/导入的订单、枫页、趣头条订单发货可无需运单号
    if (currentShopType === ShopType.MANUAL || currentShopType === ShopType.INDEPENDENT_MANUAL) {
        return {
            canShipOrders: selectedOrders,
            errorDataSource,
        };
    }

    selectedOrders.forEach((selectedOrder) => {
        const errorMessage = shipValidate(currentShopType, selectedOrder.waybillNo, selectedOrder.logisticsCompany);

        const orderIndex = allOrders.findIndex((it) => it.id === selectedOrder.id);

        let orderOriginalIds = selectedOrder.originalId;

        if (selectedOrder.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
            orderOriginalIds = selectedOrder.childOrders.map((it) => it.originalId).join(",");
        }

        if (errorMessage) {
            errorDataSource.push({
                orderId: selectedOrder.id,
                orderIndex,
                orderOriginalIds,
                errorMessage,
            });
        } else {
            canShipOrders.push(selectedOrder);
        }
    });

    return {
        canShipOrders,
        errorDataSource,
    };
}

function buildUnSupportPreShipErrorData(
    orders: OrderDTO[],
    shop: ShopDTO,
    allOrders: OrderDTO[],
): ShipWaybillValidateErrorData[] {
    const errorDataSource: ShipWaybillValidateErrorData[] = [];

    orders.forEach((selectedOrder) => {
        const errorMessage = `${ShopTypeNameMap[shop.type]}不支持预发货`;

        const orderIndex = allOrders.findIndex((it) => it.id === selectedOrder.id);

        let orderOriginalIds = selectedOrder.originalId;

        if (selectedOrder.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
            orderOriginalIds = selectedOrder.childOrders.map((it) => it.originalId).join(",");
        }

        errorDataSource.push({
            orderId: selectedOrder.id,
            orderIndex,
            orderOriginalIds,
            errorMessage,
        });
    });

    return errorDataSource;
}

// 验证单个店铺的运单
async function validateShopWaybill(
    shopOrders: OrderDTO[],
    globalStore: typeof GlobalStore,
    allOrders: OrderDTO[],
    isPreShipShopType: GetWaybillValidatorParams["isPreShipShopType"],
): Promise<WaybillValidationResult> {
    const shopId = shopOrders[0].shopId;
    const shop = globalStore.shopMapping.get(shopId);
    const shopType = shop?.type;

    if (isPreShipShopType) {
        if (!isPreShipShopType(shopType)) {
            const errors = buildUnSupportPreShipErrorData(shopOrders, shop, allOrders);
            return { validOrders: [], errors };
        }
    }

    const waybillResult = getValidateWaybillNoErrorDataSource(shopType, allOrders, shopOrders);
    return {
        validOrders: waybillResult.canShipOrders,
        errors: waybillResult.errorDataSource,
    };
}

// 处理所有店铺的运单验证组
async function processShopWaybillGroups(
    orders: OrderDTO[],
    globalStore: typeof GlobalStore,
    allOrders: OrderDTO[],
    isPreShipShopType: GetWaybillValidatorParams["isPreShipShopType"],
): Promise<WaybillValidationResult> {
    const shopGroups = ordersGroupByShopId(orders);

    let canShipOrders: OrderDTO[] = [];
    const errors: ShipWaybillValidateErrorData[] = [];

    for (const shopOrders of shopGroups) {
        const shopResult = await validateShopWaybill(shopOrders, globalStore, allOrders, isPreShipShopType);
        canShipOrders.push(...shopResult.validOrders);
        errors.push(...shopResult.errors);
    }

    return { validOrders: canShipOrders, errors };
}

export interface GetWaybillValidatorParams {
    onWaybillError: (errors: ShipWaybillValidateErrorData[], validOrders: OrderDTO[]) => Promise<boolean>;
    allOrders: OrderDTO[];
    /** 预发货检验中使用 */
    isPreShipShopType?: (shopType: ShopType) => boolean;
}

export function getWaybillValidator(params: GetWaybillValidatorParams): ShipValidator {
    const { onWaybillError, isPreShipShopType, allOrders } = params;

    return async (orders: OrderDTO[], rule: ShipValidationRule, globalStore: typeof GlobalStore) => {
        const validationResult = await processShopWaybillGroups(orders, globalStore, allOrders, isPreShipShopType);

        if (validationResult.errors.length > 0) {
            if (onWaybillError) {
                const next = await onWaybillError(validationResult.errors, validationResult.validOrders);

                if (!next) {
                    return buildValidationFailedResult("用户取消操作");
                }
            }
        }

        return buildValidationSuccessfulResult(validationResult.validOrders);
    };
}
